import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import { Thead, Th, Td, LargeOnly, SmallOnly, H3, Tr } from "commons";
import { convertToMoney } from "commons/src/Money";
import { percentageTo2DecimalPlaces } from "commons/src/helpers/FormattingHelpers";
import { isPrintMode } from "commons/src/QueryParameter";

import { Investment } from "./Investment";
import { FolioTable } from "./FolioTable";
import { TableAccordion } from "./TableAccordion";
import { Card } from "./Card";
import { TotalRow } from "./TotalRow";

export function Investments({ isCombined, accountNum, funds, totalValue, pageBreakBefore }) {
    const getPercentageOfInvestment = fund =>
        isCombined
            ? percentageTo2DecimalPlaces(fund.percentageOfPortfolio)
            : percentageTo2DecimalPlaces(fund.percentageOfAccount);

    const getInvestments = items =>
        items.map(item => (
            <Investment
                key={item.isin}
                longName={item.asset}
                value={convertToMoney(item.value)}
                split={getPercentageOfInvestment(item)}
                isin={item.isin}
                unit={item.units}
                price={item.price}
                fundFactSheetAvailable={item.fundFactSheetAvailable}
                accountNum={accountNum}
            />
        ));

    const totalRow = () => (
        <TotalRow key="total-row">
            <Td>Total</Td>
            <LargeOnly>
                <Td align="right" colSpan="4">
                    100%
                </Td>
            </LargeOnly>
            <Td align="right">{totalValue}</Td>
        </TotalRow>
    );

    const allInvestments = getInvestments(funds);

    return (
        <Container padding="1rem" isCombined={isCombined} pageBreakBefore={pageBreakBefore}>
            <H3>Investments</H3>
            <InvestmentsTable data-testid="investments-table" dense={isPrintMode()}>
                <Thead>
                    <LargeOnly enabled={!isPrintMode()}>
                        <Tr>
                            <Th align="left" padding="8px 0">
                                Name
                            </Th>
                            <Th align="right" padding="8px 0">
                                ISIN
                            </Th>
                            <Th align="right" padding="8px 0">
                                Quantity
                            </Th>
                            <Th align="right" padding="8px 0">
                                Last price
                            </Th>
                            <Th align="right" padding="8px 0">
                                Split
                            </Th>
                            <Th align="right" padding="8px 0">
                                Value
                            </Th>
                        </Tr>
                    </LargeOnly>

                    <SmallOnly>
                        <Tr>
                            <Th padding="0" align="left">
                                Name
                            </Th>
                            <Th padding="0" align="right">
                                Value
                            </Th>
                        </Tr>
                    </SmallOnly>
                </Thead>

                <LargeOnly enabled={!isPrintMode()}>
                    <tbody>{[...allInvestments, totalRow()]}</tbody>
                </LargeOnly>

                <SmallOnly>
                    <tbody>
                        {allInvestments.slice(0, 3)}
                        <TableAccordion content={[...allInvestments.slice(3), totalRow()]} />
                    </tbody>
                </SmallOnly>
            </InvestmentsTable>
        </Container>
    );
}

const Container = styled(Card)`
    @media print {
        page-break-before: ${props => props.pageBreakBefore};
        page-break-after: always;
        padding: 1rem 1rem 0 1rem;
    }
`;

const InvestmentsTable = styled(FolioTable)`
    td,
    th {
        padding: 0 1rem;
    }

    @media print {
        td {
            padding: 5px;
        }

        th {
            padding: 0 0.2rem;
        }
    }
`;

Investments.propTypes = {
    isCombined: PropTypes.bool,
    funds: PropTypes.arrayOf(
        PropTypes.shape({
            fund: PropTypes.shape({
                asset: PropTypes.string.isRequired,
                isin: PropTypes.string.isRequired,
                price: PropTypes.number.isRequired,
                percentageOfAccount: PropTypes.number.isRequired,
                percentageOfPortfolio: PropTypes.number.isRequired,
                units: PropTypes.number.isRequired,
                value: PropTypes.number.isRequired
            })
        })
    ),
    accountNum: PropTypes.string,
    totalValue: PropTypes.string,
    pageBreakBefore: PropTypes.string
};
